import { SERIES, TRACK, soft, LABEL, AXIS } from './chartColors.js';

// T330 — time-in-stage as a range bar instead of five numbers in a row.
//
// The table gave min / median / p90 / max per transition and left the reader to
// hold four numbers in their head to picture one distribution. A bar shows the
// spread at a glance: where the middle sits inside it, and how far the tail
// runs.
//
// The honesty rule is the one the table already had, kept verbatim: below
// MIN_N, the median and p90 are NOT drawn. A percentile over two observations
// is a number dressed as a statistic, and a whisker makes it look surveyed.
// Those rows get the grey min–max range only, which is a fact about the data
// rather than an inference from it.
//
// One shared scale across all transitions, so "screening → interview takes far
// longer" is readable as a longer bar rather than requiring the axis labels to
// be compared. Per-row scaling would make every row look the same length.

/** Position of `v` on a 0..max scale, as a percentage. */
export function posPct(v, max) {
  if (v == null || !(max > 0)) return null;
  return Math.max(0, Math.min(100, (v / max) * 100));
}

export default function BoxRange({ rows = [], minN = 3, unit = 'd' }) {
  const withData = rows.filter((r) => r.n > 0);
  if (!withData.length) return null;

  // The shared ceiling. `max` is the longest observed transition anywhere in
  // the set, so every bar is drawn against the same ruler.
  const ceiling = Math.max(...withData.map((r) => r.max ?? 0), 1);

  return (
    <div className="space-y-2">
      {withData.map((r) => {
        const readable = r.n >= minN;
        const left = posPct(r.min, ceiling) ?? 0;
        const right = posPct(r.max, ceiling) ?? 0;
        const med = readable ? posPct(r.median, ceiling) : null;
        const p90 = readable ? posPct(r.p90, ceiling) : null;
        const label = `${r.from} → ${r.to}`;
        return (
          <div key={label} className="flex items-center gap-3">
            <span className="w-40 shrink-0 truncate text-xs text-slate-400" title={label}>{label}</span>
            <div
              className="relative h-6 flex-1 rounded"
              style={{ background: TRACK }}
              role="img"
              aria-label={
                readable
                  ? `${label}: ${r.n} transitions, ${r.min} to ${r.max} ${unit}, median ${r.median}, 90th percentile ${r.p90}`
                  : `${label}: ${r.n} transitions, ${r.min} to ${r.max} ${unit}. Too few to give a median.`
              }
            >
              {/* The observed range. Muted when the row is below the floor, so a
                  thin row reads as thin without having to be tiny. */}
              <div
                className="absolute inset-y-1 rounded"
                style={{
                  left: `${left}%`,
                  width: `${Math.max(1.5, right - left)}%`,
                  background: readable ? soft(SERIES.accent, 45) : soft(SERIES.accent, 18),
                }}
              />
              {p90 != null && (
                <div
                  className="absolute inset-y-0.5 w-px"
                  style={{ left: `${p90}%`, background: AXIS }}
                  title={`90th percentile ${r.p90}${unit}`}
                />
              )}
              {med != null && (
                <div
                  className="absolute inset-y-0 w-[2px] rounded"
                  style={{ left: `${med}%`, background: SERIES.accent }}
                  title={`median ${r.median}${unit}`}
                />
              )}
            </div>
            <span className="w-28 shrink-0 text-right text-[11px] tabular-nums" style={{ color: LABEL }}>
              {readable
                ? `${r.median}${unit} med · n=${r.n}`
                : `n=${r.n} — too few`}
            </span>
          </div>
        );
      })}
      <p className="pt-1 text-[10px] text-slate-500">
        Bar = observed min to max, on one shared scale. Tick = median, hairline = 90th percentile.
        Rows under {minN} transitions show the range only — a percentile over two observations is
        not a percentile.
      </p>
    </div>
  );
}
